// "DFT - Illustrative Projects": finished Quantum ESPRESSO runs of the DFT module (the pilot's ten materials) with what
// was chosen and why, what the pilot taught, and "try this": a new DFT project with one thing changed, ready to run.
// Opening one gives a DFT project of the user's own holding the finished run (bands, DOS, summary, provenance).
import { Alert, Badge, Button, Card, Group, Loader, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorBox } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { useMe } from "../lib/hooks";
import { openExample, resultLine, tryExample, useExample, useExamples, type Example, type ExampleTry } from "../lib/examples";
import { Specialities } from "./Examples";
import { examplesPath } from "../lib/modules";

export const DFT_EXAMPLES_TITLE = "DFT - Illustrative Projects";

export function DftExamplesPage() {
  const { data: all, error, isLoading } = useExamples();
  const data = all?.filter((e) => e.product === "dft");
  const nav = useNavigate();
  return (
    <>
      <PageTitle title={DFT_EXAMPLES_TITLE} />
      <Text size="sm" c="dimmed" mb="md" maw={900}>
        Finished Quantum ESPRESSO calculations of the DFT module, each compared with published values: semiconductors and
        insulators, a metal, magnets (ferro- and antiferromagnetic, DFT+U), a 2D layer and a topological insulator with
        spin-orbit coupling. Each guide says what was chosen and why, and what went wrong on the way. Open one to look at its
        bands, density of states and run; "Try this" makes a new project with one thing changed, ready to run.
      </Text>
      <ErrorBox error={error} />
      {isLoading && <Loader size="sm" />}
      {data && data.length === 0 && <Text size="sm" c="dimmed">None on this server yet.</Text>}
      {data && data.length > 0 && (
        <Card withBorder p={0}>
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="sm" data-testid="dft-examples-table">
              <Table.Thead>
                <Table.Tr><Table.Th>#</Table.Th><Table.Th>Project</Table.Th><Table.Th>Material</Table.Th><Table.Th>What it shows</Table.Th>
                  <Table.Th>Result</Table.Th><Table.Th>Reference</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((e, i) => (
                  <Table.Tr key={e.id} style={{ cursor: "pointer" }} onClick={() => nav(examplesPath("dft", e.id))}>
                    <Table.Td>{i + 1}</Table.Td>
                    <Table.Td><Link to={examplesPath("dft", e.id)} onClick={(ev) => ev.stopPropagation()}><Text fw={600} size="sm">{e.name}</Text></Link>
                      {e.project_id && <Badge size="xs" color="teal" variant="light" ml={6}>opened</Badge>}</Table.Td>
                    <Table.Td><Text size="sm">{e.material}</Text></Table.Td>
                    <Table.Td><Specialities items={e.speciality} /></Table.Td>
                    <Table.Td><Text size="sm">{resultLine(e.result)}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{e.reference}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </>
  );
}

/** "Try this": one card per variation; each makes a new DFT project with the recipe changed. */
export function TryList({ exampleId, tries, note }: { exampleId: string; tries: ExampleTry[]; note?: string }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: me } = useMe();
  const make = useMutation({
    mutationFn: (t: string) => tryExample(exampleId, t),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      nav(`/projects/${p.id}`);
    },
  });
  if (!tries.length) return null;
  return (
    <Stack gap="xs" data-testid="try-list">
      <Text fw={600}>Try this</Text>
      <Text size="xs" c="dimmed">{note ?? <>Each makes a new DFT project with one thing changed. Compare its checks and estimated time first,
        then run it{me && !me.limits["dft.qe_run"] ? " (running needs the Advanced plan; you can prepare and check it)" : ""}.</>}</Text>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
        {tries.map((t) => (
          <Card key={t.id} withBorder p="xs">
            <Stack gap={4}>
              <Text size="sm" fw={600}>{t.title}</Text>
              <Text size="xs">{t.about}</Text>
              <Group><Button size="compact-xs" variant="light" loading={make.isPending && make.variables === t.id}
                onClick={() => make.mutate(t.id)} aria-label={`try ${t.id}`}>Make this project</Button></Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
      <ErrorBox error={make.error} />
    </Stack>
  );
}

function OpenButton({ ex }: { ex: Example }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const open = useMutation({
    mutationFn: () => openExample(ex.id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["examples"] });
      nav(`/projects/${p.id}`);
    },
  });
  return (
    <Stack gap={4} align="flex-start">
      <Button onClick={() => open.mutate()} loading={open.isPending} data-testid="open-dft-example">
        {ex.project_id ? "Continue in my project" : "Open as my project"}</Button>
      <ErrorBox error={open.error} />
    </Stack>
  );
}

export function DftExampleDetail() {
  const { id } = useParams();
  const { data: ex, error, isLoading } = useExample(id);
  if (isLoading) return <Loader size="sm" />;
  if (error || !ex) return <ErrorBox error={error ?? new Error("Not found")} />;
  const r = ex.result ?? {};
  return (
    <>
      <PageTitle title={ex.name}>
        <Button component={Link} to={examplesPath("dft")} variant="default">All DFT illustrative projects</Button>
      </PageTitle>
      <Stack gap="md" maw={1000}>
        <Card withBorder>
          <Stack gap={6}>
            <Text size="sm"><b>Material:</b> {ex.material}</Text>
            <Specialities items={ex.speciality} />
            <Text size="sm" c="dimmed">{ex.summary}</Text>
            <Alert color={ex.check?.startsWith("OK") ? "teal" : "yellow"} p="xs" data-testid="dft-example-result">
              <Text size="sm"><b>Result:</b> {resultLine(r)}{r.qe_version ? ` · Quantum ESPRESSO ${r.qe_version}` : ""}
                {r.seconds ? ` · ${r.seconds < 90 ? `${Math.round(r.seconds)} s` : `${(r.seconds / 60).toFixed(1)} min`} on ${r.nprocs ?? "?"} processes` : ""}</Text>
              <Text size="sm"><b>Reference:</b> {ex.reference}{ex.check ? ` · ${ex.check}` : ""}</Text>
            </Alert>
            {!!ex.runs?.length && <Text size="xs" data-testid="dft-example-runs">With {ex.runs.length + 1} runs: this one and{" "}
              {ex.runs.map((x) => x.title).join(", ")} - the Defect levels and Formation energy tabs use them.</Text>}
            <OpenButton ex={ex} />
            <Text size="xs" c="dimmed">Opening it costs no Quantum ESPRESSO time: the finished run is copied into your project.</Text>
          </Stack>
        </Card>
        <Card withBorder data-testid="example-guide"><Markdown source={ex.guide ?? ""} /></Card>
        <Card withBorder><TryList exampleId={ex.id} tries={ex.tries ?? []} /></Card>
      </Stack>
    </>
  );
}
